import { useAuth } from '@/context/AuthContext';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { fetchUserProfile } from '@/lib/api';

type UnitSystem = 'imperial' | 'metric';

/**
 * Hook to get unit system from user profile.
 * Fetches /me/profile (cached, long staleTime) to read unit_system.
 * Falls back to 'metric' if profile is unavailable.
 */
export function useUnitSystem() {
  const { user } = useAuth();

  // Fetch profile with long cache — unit_system lives on /me/profile, not /me
  const { data: profileData } = useAuthenticatedQuery({
    queryKey: ['userProfile'],
    queryFn: fetchUserProfile,
    staleTime: 30 * 60 * 1000, // 30 min — unit system rarely changes
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });

  const profileUnit = (profileData as { unit_system?: UnitSystem } | null)?.unit_system
    ?? (profileData as { unitSystem?: UnitSystem } | null)?.unitSystem;

  // Fallback chain: profile → auth user → 'metric'
  const authUnit = (user as { unitSystem?: UnitSystem })?.unitSystem;
  const unitSystem: UnitSystem = profileUnit ?? authUnit ?? 'imperial';

  const convertDistance = (km: number): { value: number; unit: string } => {
    if (unitSystem === 'imperial') {
      return {
        value: Math.round((km * 0.621371) * 10) / 10, // Convert to miles, round to 1 decimal
        unit: 'mi',
      };
    }
    return {
      value: Math.round(km * 10) / 10, // Round to 1 decimal
      unit: 'km',
    };
  };

  const convertElevation = (meters: number): { value: number; unit: string } => {
    if (unitSystem === 'imperial') {
      return {
        value: Math.round((meters * 3.28084) * 10) / 10, // Convert to feet, round to 1 decimal
        unit: 'ft',
      };
    }
    return {
      value: Math.round(meters * 10) / 10, // Round to 1 decimal
      unit: 'm',
    };
  };

  const convertWeight = (kg: number): { value: number; unit: string } => {
    if (unitSystem === 'imperial') {
      return {
        value: Math.round((kg * 2.20462) * 10) / 10, // Convert to lbs, round to 1 decimal
        unit: 'lbs',
      };
    }
    return {
      value: Math.round(kg * 10) / 10, // Round to 1 decimal
      unit: 'kg',
    };
  };

  const convertHeight = (cm: number): { value: number; unit: string } => {
    if (unitSystem === 'imperial') {
      const totalInches = cm / 2.54;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return {
        value: parseFloat(`${feet}.${inches}`),
        unit: 'ft',
      };
    }
    return {
      value: Math.round(cm),
      unit: 'cm',
    };
  };

  const convertPace = (minPerKm: number): { value: number; unit: string } => {
    if (unitSystem === 'imperial') {
      // Convert min/km to min/mile: multiply by 1.60934
      return {
        value: Math.round(minPerKm * 1.60934 * 100) / 100, // Round to 2 decimals
        unit: 'min/mi',
      };
    }
    return {
      value: Math.round(minPerKm * 100) / 100, // Round to 2 decimals
      unit: 'min/km',
    };
  };

  const formatDistance = (converted: { value: number; unit: string }): string => {
    return `${converted.value} ${converted.unit}`;
  };

  /** Format pace from min/km to "M:SS /mi" or "M:SS /km" based on unit system. */
  const formatPaceFromMinPerKm = (minPerKm: number): string => {
    const converted = convertPace(minPerKm);
    const totalSeconds = Math.round(converted.value * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const suffix = converted.unit === 'min/mi' ? '/mi' : '/km';
    return `${mins}:${secs.toString().padStart(2, '0')} ${suffix}`;
  };

  /**
   * Parse and format pace string (e.g. "5 min/km", "5.48 min/km", "5:29/km") to unit-aware display.
   * Returns undefined if parsing fails.
   */
  const formatPaceFromString = (paceString: string | undefined): string | undefined => {
    if (!paceString || typeof paceString !== 'string') return undefined;
    const trimmed = paceString.trim();
    const colonMatch = trimmed.match(/^(\d+):(\d{2})/);
    if (colonMatch) {
      const mins = parseInt(colonMatch[1], 10);
      const secs = parseInt(colonMatch[2], 10);
      const minPerKm = mins + secs / 60;
      return formatPaceFromMinPerKm(minPerKm);
    }
    const numMatch = trimmed.match(/([\d.]+)/);
    if (!numMatch) return trimmed;
    const minPerKm = parseFloat(numMatch[1]);
    if (Number.isNaN(minPerKm)) return trimmed;
    const converted = convertPace(minPerKm);
    return `${converted.value.toFixed(2)} ${converted.unit}`;
  };

  const paceUnit = unitSystem === 'imperial' ? '/mi' : '/km';

  /**
   * Convert pace range string (e.g. "6:30–7:00", "< 4:40") from min/km to unit-aware display.
   */
  const formatPaceRange = (paceRange: string): string => {
    if (unitSystem === 'metric') return paceRange;
    const convertOne = (m: number, s: number): string => {
      const minPerKm = m + s / 60;
      const minPerMi = minPerKm * 1.60934;
      const totalSec = Math.round(minPerMi * 60);
      const nm = Math.floor(totalSec / 60);
      const ns = totalSec % 60;
      return `${nm}:${ns.toString().padStart(2, '0')}`;
    };
    return paceRange.replace(/(\d+):(\d{2})/g, (_, m, s) =>
      convertOne(parseInt(m, 10), parseInt(s, 10))
    );
  };

  return {
    unitSystem,
    isLoading: false, // Always ready since we have a default
    convertDistance,
    convertElevation,
    convertWeight,
    convertHeight,
    convertPace,
    formatDistance,
    formatPaceFromMinPerKm,
    formatPaceFromString,
    formatPaceRange,
    paceUnit,
  };
}

